import { useTranslations } from '../hooks/useTranslations';

/**
 * Translates category names based on the current language
 * @param categoryName - The category name to translate
 * @returns The translated category name or the original if no translation found
 */
export const translateCategoryName = (categoryName: string, translations?: ReturnType<typeof useTranslations>): string => {
  if (!categoryName) return 'Unknown';
  
  // If translations are not provided, we'll need to get them from context
  // For now, we'll create a function that can be called with translations
  const lowerName = categoryName.toLowerCase().trim();
  
  // Map common category names to translation keys
  const categoryTranslationMap: { [key: string]: string } = {
    'bakeries': 'bakeries',
    'bakery': 'bakeries',
    'sweet shops': 'sweetShops',
    'sweet-shops': 'sweetShops',
    'sweets': 'sweetShops',
    'home food': 'homeFood',
    'home-food': 'homeFood',
    'homefood': 'homeFood',
    'pickles': 'pickles',
    'pickle': 'pickles',
    'dairy': 'dairy',
    'fruits & veg': 'fruitsVeg',
    'fruits-veg': 'fruitsVeg',
    'fruits vegetables': 'fruitsVeg',
    'fruits': 'fruitsVeg',
    'vegetables': 'fruitsVeg',
    'flowers': 'flowers',
    'flower': 'flowers',
    'meat': 'meat',
    'kirana': 'kirana',
    'organic': 'organic',
    'rice & grains': 'riceGrains',
    'rice-grains': 'riceGrains',
    'rice grains': 'riceGrains',
    'rice': 'riceGrains',
    'grains': 'riceGrains',
    'dry fruits': 'dryFruits',
    'dry-fruits': 'dryFruits',
    'pharmacy': 'pharmacy',
    'ayurvedic': 'ayurvedic',
    'ayurveda': 'ayurvedic',
    'dental care': 'dentalCare',
    'dental-care': 'dentalCare',
    'dental': 'dentalCare',
    'wellness': 'wellness',
    'health': 'wellness',
    'medical': 'wellness',
    'grocery': 'groceries',
    'groceries': 'groceries',
    'restaurant': 'restaurant',
    'restaurants': 'restaurant',
    'specialty': 'specialty',
    'specialties': 'specialty',
  };
  
  // If translations are provided, use them
  if (translations) {
    // Check for exact match first
    if (categoryTranslationMap[lowerName]) {
      const translationKey = categoryTranslationMap[lowerName];
      return (translations as any)[translationKey] || categoryName;
    }
    
    // Check for partial matches
    for (const [key, translationKey] of Object.entries(categoryTranslationMap)) {
      if (lowerName.includes(key) || key.includes(lowerName)) {
        return (translations as any)[translationKey] || categoryName;
      }
    }
  }
  
  // Return original name if no translation found
  return categoryName;
};

/**
 * Translates shop type names based on the current language
 * @param shopType - The shop type to translate (e.g., 'bakery', 'grocery')
 * @param translations - Optional translations object
 * @returns The translated shop type name
 */
export const translateShopType = (shopType: string, translations?: ReturnType<typeof useTranslations>): string => {
  if (!shopType) return '';
  
  const lowerType = shopType.toLowerCase().trim();
  
  // Map shop types to translation keys
  const shopTypeTranslationMap: { [key: string]: string } = {
    'grocery': 'groceryStore',
    'groceries': 'groceryStore',
    'kirana': 'groceryStore',
    'mart': 'groceryStore',
    'pharmacy': 'pharmacy',
    'pharmacies': 'pharmacy',
    'bakery': 'bakery',
    'bakeries': 'bakery',
    'baker': 'bakery',
    'electronics': 'electronics',
    'clothing': 'clothing',
    'medical': 'medical',
    'restaurant': 'restaurant',
    'restaurants': 'restaurant',
    'specialty': 'specialtyStore',
    'specialties': 'specialtyStore',
    'general': 'generalStore',
  };
  
  if (translations) {
    const translationKey = shopTypeTranslationMap[lowerType];
    if (translationKey) {
      return (translations as any)[translationKey] || shopType;
    }
  }
  
  // Return capitalized original if no translation found
  return shopType.charAt(0).toUpperCase() + shopType.slice(1);
};

/**
 * Hook to get the translateCategoryName function with translations
 */
export const useCategoryTranslation = () => {
  const t = useTranslations();
  
  return (categoryName: string) => translateCategoryName(categoryName, t);
};

/**
 * Hook to get the translateShopType function with translations
 */
export const useShopTypeTranslation = () => {
  const t = useTranslations();
  
  return (shopType: string) => translateShopType(shopType, t);
};

